import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import CartGlyph from './CartGlyph';
import useContrastingInk from './useContrastingInk';

// How far the badge is pulled back over the glyph's top-right corner. 8px lands
// the fill's left edge at 17px against a measured ink edge (the basket's
// rightmost stroke pixel) of ~21.6px, so it occludes the rim's tip and reads as
// an attribute OF the cart. The item dots stop at ~15px and stay clear.
const BADGE_TUCK_PX = 8;

// In Windows High Contrast the ring below is dropped (box-shadow is not
// rendered) while the badge itself survives via forced-color-adjust: none. With
// nothing separating the two, the full tuck would eat the rim tip outright, so
// fall back to a grazing overlap where the fill is merely tangent to it.
const BADGE_TUCK_FORCED_COLORS_PX = 4;

export const BADGE_MAX = 99;

// Matches the existing PrimaryButton blue in v3Styles.jsx.
const BADGE_ACCENT = '#0b76b7';

export function cartLabel(count: number): string {
  return `Shopping cart, ${count} ${count === 1 ? 'item' : 'items'}`;
}

const Glyph = styled.span<{ $ink: string }>`
  color: ${({ $ink }) => $ink};
  display: inline-flex;
  /* 15px unconditionally, which is exact parity with the deleted
     .right__cart rule (25px glyph + 15px = a 40px shrink-to-fit
     .right__cart-container). Because the badge is left-anchored and tucked
     back over the glyph's corner, its footprint past the glyph is small
     enough that 15px absorbs the 1- and 2-digit cases outright (8px and
     12.9px of overhang, ring included); the rare 3-character "99+" runs to
     18.7px and leans the difference into .right__cart-container's own
     margin, which is harmless since the cart is the rightmost element (see
     .right__cart-container / .mobileCartContainer in navbar.scss).
     Reserving unconditionally is deliberate: it means the icon never moves
     when the count goes 0 -> 1, and it restores the pre-ENG-2550 geometry
     exactly in the empty-cart state, which is what most parents see most
     of the time. */
  margin-right: 15px;
  position: relative;

  /* Background-image icons are dropped in Windows High Contrast; an SVG in
     currentColor can follow the system palette instead. */
  @media (forced-colors: active) {
    color: CanvasText;
  }
`;

const Badge = styled.span<{ $surface: string }>`
  background: ${BADGE_ACCENT};
  border-radius: 999px;
  /* Ring in the measured header color, so the badge stays separated from the
     glyph on black, white, and grey headers alike. */
  box-shadow: 0 0 0 2px ${({ $surface }) => $surface};
  /* Explicit rather than inherited. The app loads Bootstrap 3, which sets
     a global * { box-sizing: border-box }, but Storybook's stylesheet does
     not - so without this the badge renders 8px wider in Storybook than in
     production and any geometry tuned against a story would be wrong. */
  box-sizing: border-box;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  height: 14px;
  letter-spacing: -0.1px;
  line-height: 14px;
  min-width: 14px;
  padding: 0 4px;
  position: absolute;
  /* Anchored from the left so the pill grows rightward into empty header
     space as the count widens, instead of right-anchoring and eating
     leftward into the basket rim (which at "99+" swallowed the rim and both
     item dots entirely).
     left resolves against Glyph's padding-box width, which is exactly the
     glyph's size prop (Glyph is inline-flex with the SVG as its only
     in-flow child, since this Badge is out of flow). The pullback tucks the
     badge onto the cart's top-right corner instead of floating it clear:
     occlusion is what makes the badge read as an attribute OF the cart
     rather than a separate object beside it. See BADGE_TUCK_PX for why 8px.
     The pullback is fixed rather than proportional, so the overlap stays
     constant at every digit count - the pill only ever grows rightward. */
  left: calc(100% - ${BADGE_TUCK_PX}px);
  right: auto;
  text-align: center;
  top: -7px;

  @media (forced-colors: active) {
    background: Highlight;
    color: HighlightText;
    forced-color-adjust: none;
    left: calc(100% - ${BADGE_TUCK_FORCED_COLORS_PX}px);
  }
`;

const CartLink = styled(Link)`
  /* Bootstrap 3 styles every anchor with
       a:focus { outline: 5px auto -webkit-focus-ring-color; outline-offset: -2px }
     (_scaffolding.scss, via the tab-focus mixin). That is a plain :focus rule
     rather than :focus-visible, so it fires on mouse click as well as on
     keyboard, and it lands badly here: in the desktop header this anchor sits
     in .navbar-nav > li, so it also takes Bootstrap's horizontal padding, and
     because its only child floats it collapses to a zero-height box. The ring
     Chrome paints from that is a rounded rectangle standing off the glyph
     rather than tracing it.
     Drop it here and put a real ring on the glyph instead, for keyboard users
     only - clicking the cart should not leave a box behind. */
  &:focus {
    outline: none;
  }

  /* currentColor inside Glyph is the contrast-picked ink, so the ring tracks
     the header exactly the way the glyph does and stays readable on the black,
     white and grey headers alike without a second measurement. */
  &:focus-visible ${Glyph} {
    border-radius: 3px;
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

interface CartButtonProps {
  count: number;
}

const CartButton: React.FC<CartButtonProps> = ({ count }) => {
  const { ref, ink, surface } = useContrastingInk<HTMLSpanElement>();
  const safeCount = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;

  return (
    <CartLink
      to="/v3/cart"
      className="link-to-cart"
      aria-label={cartLabel(safeCount)}
    >
      <div className="right__cart-container cursor-pointer">
        <Glyph ref={ref} $ink={ink}>
          <CartGlyph />
          {safeCount > 0 && (
            <Badge $surface={surface} data-testid="cart-badge">
              {safeCount > BADGE_MAX ? `${BADGE_MAX}+` : safeCount}
            </Badge>
          )}
        </Glyph>
      </div>
    </CartLink>
  );
};

export default CartButton;
